import type { LogQueryParams } from '@central/api';
import {
  Card,
  formatDateTime,
  LogLines,
  logFilterErrors,
  logLevelOptions,
  logRangeBounds,
  logRangeOptions,
  type LogLevel,
  type LogRange,
} from '@central/ui';
import { Alert, Autocomplete, Button, Loader, Select, TextInput } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { keys, useApi } from '../api';
import { errorMessage } from '../lib/errors';
import { muted } from '../lib/labels';
import { LogRateChart } from './LogRateChart';
import { Empty } from './parts';

/** The filters besides the environment; `window` is a fixed time range a link named. */
export interface LogFilters {
  service: string;
  level: '' | LogLevel;
  /** A window ending at search time, or 'window' for the fixed one. */
  range: LogRange | 'window';
  contains: string;
  window?: { start: string; end: string };
}

const defaultFilters: LogFilters = { service: '', level: 'warn', range: '1h', contains: '' };

function toParams(environmentId: string, d: LogFilters): LogQueryParams {
  return {
    environmentId,
    service: d.service || undefined,
    level: d.level || undefined,
    contains: d.contains || undefined,
    ...(d.range === 'window' && d.window ? d.window : logRangeBounds(d.range as LogRange)),
  };
}

/** "Oct 21, 10:03–10:18 UTC" for a fixed window. */
const windowLabel = (w: { start: string; end: string }) =>
  `${formatDateTime(w.start)}–${new Date(w.end).toISOString().slice(11, 16)} UTC`;

/**
 * Log search over one environment: service, level, time and text filters,
 * lines per minute, and the matching lines. `searchAtOnce` runs the search
 * with the starting filters (`initial`, else the defaults) for the
 * environment given at mount; choosing another environment
 * clears the results until the next search. `leading` fields go first in
 * the filter row. The server audits every search.
 */
export function LogSearch({
  environmentId,
  searchAtOnce,
  leading,
  idleHint,
  initial,
  onChange,
}: {
  /** '' while no environment is chosen. */
  environmentId: string;
  searchAtOnce: boolean;
  leading?: ReactNode;
  idleHint: string;
  /** Filters to start from instead of the defaults; read at mount. */
  initial?: Partial<LogFilters>;
  /** Called with the filters as they are edited (not yet searched). */
  onChange?: (f: LogFilters) => void;
}) {
  const api = useApi();
  const [draft, setDraft] = useState<LogFilters>(() => ({ ...defaultFilters, ...initial }));
  const [fieldErrors, setFieldErrors] = useState<ReturnType<typeof logFilterErrors>>({});
  const [applied, setApplied] = useState<LogQueryParams | undefined>(() =>
    searchAtOnce && environmentId ? toParams(environmentId, draft) : undefined,
  );
  useEffect(() => onChange?.(draft), [draft, onChange]);
  const [shownFor, setShownFor] = useState(environmentId);
  if (shownFor !== environmentId) {
    setShownFor(environmentId);
    setApplied(undefined);
  }

  const result = useQuery({
    queryKey: keys.logs(applied ?? {}),
    queryFn: () => api.logs.query(applied!),
    enabled: !!applied,
    retry: false,
  });
  const rate = useQuery({
    queryKey: keys.logRate(applied ?? {}),
    queryFn: () => api.logs.rate(applied!),
    enabled: !!applied,
    retry: false,
  });
  // Service labels seen in results, per environment: labels belong to one site's stack.
  const [seenServices, setSeenServices] = useState<Record<string, string[]>>({});
  useEffect(() => {
    const env = applied?.environmentId;
    const s = result.data?.lines.flatMap((l) => (l.service ? [l.service] : [])) ?? [];
    if (env && s.length)
      setSeenServices((prev) => ({
        ...prev,
        [env]: [...new Set([...(prev[env] ?? []), ...s])].sort(),
      }));
  }, [result.data, applied?.environmentId]);

  const set = <K extends keyof LogFilters>(k: K, v: LogFilters[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!environmentId) return;
    const errs = logFilterErrors(draft);
    setFieldErrors(errs);
    if (errs.service || errs.contains) return;
    setApplied(toParams(environmentId, draft));
  };
  const lines = result.data?.lines ?? [];

  return (
    <>
      <form onSubmit={submit} noValidate aria-label="Search logs">
        <Card>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 12,
              alignItems: 'end',
            }}
          >
            {leading}
            <Autocomplete
              label="Service"
              placeholder="All services"
              data={seenServices[environmentId] ?? []}
              value={draft.service}
              error={fieldErrors.service}
              onChange={(v) => set('service', v.trim())}
            />
            <Select
              label="Level"
              data={logLevelOptions}
              value={draft.level}
              onChange={(v) => set('level', (v ?? '') as LogFilters['level'])}
              allowDeselect={false}
            />
            <Select
              label="Time"
              data={[
                ...logRangeOptions.map(({ value, label }) => ({ value, label })),
                ...(draft.window ? [{ value: 'window', label: windowLabel(draft.window) }] : []),
              ]}
              value={draft.range}
              onChange={(v) => v && set('range', v as LogFilters['range'])}
              allowDeselect={false}
            />
            <TextInput
              label="Contains"
              value={draft.contains}
              error={fieldErrors.contains}
              onChange={(e) => set('contains', e.currentTarget.value)}
              styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
            />
            <Button
              type="submit"
              color="dark"
              disabled={!environmentId}
              loading={result.isFetching}
            >
              Search
            </Button>
          </div>
        </Card>
      </form>
      {applied && !result.error && rate.data && <LogRateChart rate={rate.data} />}
      {applied && !result.error && rate.error && (
        <Alert color="red">Line counts: {errorMessage(rate.error)}</Alert>
      )}
      <Card padded={false}>
        {!applied ? (
          <Empty>{idleHint}</Empty>
        ) : result.error ? (
          <div style={{ padding: 16 }}>
            <Alert color="red">{errorMessage(result.error)}</Alert>
          </div>
        ) : !result.data ? (
          <div style={{ padding: 16 }}>
            <Loader size="sm" aria-label="Loading" />
          </div>
        ) : lines.length === 0 ? (
          <Empty>No lines match in this time range.</Empty>
        ) : (
          <>
            <LogLines lines={lines} />
            {result.data.limited && (
              <p style={{ margin: 0, padding: '12px 20px', fontSize: 13, ...muted }}>
                Showing the newest {lines.length} lines. Narrow the search to see older ones.
              </p>
            )}
          </>
        )}
      </Card>
    </>
  );
}
